import { getAuthToken } from '../utils/authStorage';
import cookieConsentService from './cookieConsentService';

import API_BASE_URL from '../config/apiBaseUrl';

class AuthService {
  async login(email, password) {
    try {
      const analyticsConsent = cookieConsentService.canUseCategory('analytics');
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Analytics-Consent': analyticsConsent ? '1' : '0',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const retryAfterFromBody = Number(data.retry_after);
        const retryAfterFromHeader = Number(response.headers.get('Retry-After'));
        const retryAfter = Number.isFinite(retryAfterFromBody) && retryAfterFromBody > 0
          ? Math.ceil(retryAfterFromBody)
          : (Number.isFinite(retryAfterFromHeader) && retryAfterFromHeader > 0 ? Math.ceil(retryAfterFromHeader) : null);

        let message = data.message || "Échec de la connexion";
        if (retryAfter !== null) {
          message = `${message} Réessayez dans ${retryAfter} seconde${retryAfter > 1 ? 's' : ''}.`;
        }

        throw new Error(message);
      }

      if (!data.token) {
        throw new Error("Aucun token JWT reçu");
      }

      return { user: data.user, token: data.token };
    } catch (error) {
      console.error("Erreur lors du login :", error);
      throw error;
    }
  }

  async register(payload) {
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const fieldErrors = {};
      if (Array.isArray(data.errors)) {
        data.errors.forEach(err => {
          fieldErrors[err.field] = err.message; // uniquement le message
        });
      } else if (data.errors && typeof data.errors === 'object') {
        Object.entries(data.errors).forEach(([field, message]) => {
          fieldErrors[field] = message;
        });
      } else if (data.message) {
        fieldErrors.global = data.message; // pour les erreurs générales
      } else if (data.detail) {
        fieldErrors.global = data.detail;
      }

      const retryAfterFromBody = Number(data.retry_after);
      const retryAfterFromHeader = Number(response.headers.get('Retry-After'));
      const retryAfter = Number.isFinite(retryAfterFromBody) && retryAfterFromBody > 0
        ? Math.ceil(retryAfterFromBody)
        : (Number.isFinite(retryAfterFromHeader) && retryAfterFromHeader > 0 ? Math.ceil(retryAfterFromHeader) : null);

      if (retryAfter !== null) {
        const retryText = `Réessayez dans ${retryAfter} seconde${retryAfter > 1 ? 's' : ''}.`;
        fieldErrors.global = fieldErrors.global
          ? `${fieldErrors.global} ${retryText}`
          : retryText;
      }

      if (!fieldErrors.global && Object.keys(fieldErrors).length === 0) {
        fieldErrors.global = 'Impossible de traiter votre inscription pour le moment.';
      }

      throw fieldErrors;
    }

    return data;
  }

  async getCurrentUser() {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Token invalide ou expiré');
    }

    return response.json();
  }

  async verifyEmail(token) {
    const response = await fetch(`${API_BASE_URL}/users/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Erreur lors de la vérification de l\'email');
    }

    return response.json();
  }

  async requestPasswordReset(email) {
    const response = await fetch(`${API_BASE_URL}/reset-password/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || 'Erreur lors de la demande de réinitialisation');
    }

    return data;
  }

  async resetPassword(token, password, passwordConfirm) {
    const response = await fetch(`${API_BASE_URL}/reset-password/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password, passwordConfirm }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (data.errors) {
        return Promise.reject(data.errors);
      }
      throw new Error(data.message || 'Erreur lors de la réinitialisation');
    }

    return data;
  }

  async logout() {
    return Promise.resolve();
  }
}

export const authService = new AuthService();
